import { Injectable } from '@angular/core';
import type { Cell, Row, Worksheet } from 'exceljs';
import type { CashFlowItem } from './cash-flow.service';

export interface CashFlowExportFilters {
  dateFrom: string;
  dateTo: string;
  direction: string;
  category: string;
  paymentMethod: string;
  searchTerm: string;
}

@Injectable({ providedIn: 'root' })
export class CashFlowExportService {
  async export(items: CashFlowItem[], filters: CashFlowExportFilters): Promise<void> {
    const excelJsModule = (await import('exceljs')) as unknown as {
      default: typeof import('exceljs');
    };
    const workbook = new excelJsModule.default.Workbook();
    workbook.creator = 'All To Trips';
    workbook.created = new Date();
    workbook.modified = new Date();

    const worksheet = workbook.addWorksheet('รายการรับ-จ่าย', {
      properties: { defaultRowHeight: 20 },
      pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    });

    this.configureColumns(worksheet);
    this.addReportHeader(worksheet, filters, items.length);
    const tableHeaderRow = this.addTable(worksheet, items);
    worksheet.autoFilter = {
      from: { row: tableHeaderRow.number, column: 1 },
      to: { row: tableHeaderRow.number, column: 17 },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = `cash-flow-${filters.dateFrom || 'all'}-to-${filters.dateTo || 'all'}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(downloadUrl);
  }

  private configureColumns(worksheet: Worksheet): void {
    const widths = [8, 14, 22, 22, 28, 14, 22, 20, 18, 20, 25, 26, 26, 40, 18, 18, 38];
    widths.forEach((width, index) => {
      worksheet.getColumn(index + 1).width = width;
    });
  }

  private addReportHeader(
    worksheet: Worksheet,
    filters: CashFlowExportFilters,
    itemCount: number,
  ): void {
    worksheet.mergeCells('A1:Q1');
    const title = worksheet.getCell('A1');
    title.value = `รายงานรายการรับ-จ่าย ${filters.dateFrom && filters.dateTo ? ` (${filters.dateFrom} ถึง ${filters.dateTo})` : ''}`;
    title.font = { bold: true, size: 16, color: { argb: 'FF0F172A' } };
    title.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 28;

    const queryRows: Array<[string, string | number]> = [
      ['วันที่เริ่มต้น', filters.dateFrom || 'ทั้งหมด'],
      ['วันที่สิ้นสุด', filters.dateTo || 'ทั้งหมด'],
      ['รับหรือจ่าย', filters.direction],
      ['ประเภทรายการ', filters.category],
      ['ช่องทางชำระ', filters.paymentMethod],
      ['คำค้นหา', filters.searchTerm || 'ไม่ได้ระบุ'],
      ['จำนวนรายการ', itemCount],
    ];
    queryRows.forEach((values) => {
      const row = worksheet.addRow(values);
      row.getCell(1).font = { bold: true };
      row.eachCell((cell) => {
        cell.alignment = { vertical: 'middle' };
        cell.border = this.thinBorder();
      });
    });
    worksheet.addRow([]);
  }

  private addTable(worksheet: Worksheet, items: CashFlowItem[]): Row {
    const headerRow = worksheet.addRow([
      'ลำดับ',
      'วันที่',
      'เลขที่รายการ',
      'เลข Booking',
      'ชื่อลูกค้า',
      'รับหรือจ่าย',
      'ประเภทรายการ',
      'ช่องทางชำระ',
      'ค่าธรรมเนียม (%)',
      'ค่าธรรมเนียม (บาท)',
      'ผู้รับภาระค่าธรรมเนียม',
      'พนักงานที่เบิก',
      'พนักงานอนุมัติ',
      'รายละเอียด',
      'รายรับ',
      'รายจ่าย',
      'เอกสาร',
    ]);
    this.styleHeaderRow(headerRow, 17);

    items.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        this.toExcelDate(item.transactionDate),
        item.transactionNo,
        item.bookingNo,
        item.customerName,
        item.direction === 'INCOME' ? 'รายรับ' : 'รายจ่าย',
        this.categoryLabel(item.category),
        this.paymentMethodLabel(item.paymentMethod),
        item.paymentMethod === 'CREDIT_CARD' ? item.creditCardFeePercent : null,
        item.paymentMethod === 'CREDIT_CARD' ? item.creditCardFeeAmount : null,
        item.paymentMethod === 'CREDIT_CARD'
          ? this.feeResponsibilityLabel(item.creditCardFeeResponsibility)
          : '',
        item.direction === 'EXPENSE' ? item.requestedByName : '-',
        item.approvedByName,
        item.description,
        item.direction === 'INCOME' ? item.amount : null,
        item.direction === 'EXPENSE' ? item.amount : null,
        item.documentUrl ?? '',
      ]);
      row.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = this.thinBorder();
        cell.alignment = { vertical: 'top', wrapText: true };
      });
      row.getCell(2).numFmt = 'dd/mm/yyyy';
      row.getCell(9).numFmt = '0"%"';
      [10, 15, 16].forEach((column) => {
        row.getCell(column).numFmt = '#,##0.00';
      });
      this.setDocumentLink(row.getCell(17), item.documentUrl);
    });

    const incomeTotal = items
      .filter((item) => item.direction === 'INCOME')
      .reduce((total, item) => total + item.amount, 0);
    const expenseTotal = items
      .filter((item) => item.direction === 'EXPENSE')
      .reduce((total, item) => total + item.amount, 0);
    const totalRow = worksheet.addRow([
      'รวมทั้งหมด',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      incomeTotal,
      expenseTotal,
      '',
    ]);
    totalRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = { bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
      cell.border = this.thinBorder();
    });
    totalRow.getCell(15).numFmt = '#,##0.00';
    totalRow.getCell(16).numFmt = '#,##0.00';

    return headerRow;
  }

  private styleHeaderRow(row: Row, columnCount: number): void {
    row.height = 24;
    for (let column = 1; column <= columnCount; column += 1) {
      const cell = row.getCell(column);
      cell.font = { bold: true, color: { argb: 'FF0F172A' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE2E8F0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = this.thinBorder();
    }
  }

  private setDocumentLink(cell: Cell, documentUrl: string | null): void {
    if (!documentUrl) return;
    if (/^https?:\/\//i.test(documentUrl)) {
      cell.value = { text: 'เปิดเอกสาร', hyperlink: documentUrl, tooltip: documentUrl };
      cell.font = { color: { argb: 'FF0369A1' }, underline: true };
      return;
    }
    cell.value = documentUrl;
  }

  private thinBorder() {
    const side = { style: 'thin' as const, color: { argb: 'FFE2E8F0' } };
    return { top: side, left: side, bottom: side, right: side };
  }

  private toExcelDate(value: string): Date {
    const date = new Date(value);
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  private categoryLabel(category: CashFlowItem['category']): string {
    return {
      CUSTOMER_PAYMENT: 'รับชำระลูกค้า',
      EXPENSE: 'ค่าใช้จ่าย',
      COMMISSION: 'คอมมิชชั่น',
      REFUND: 'คืนเงินลูกค้า',
    }[category];
  }

  private paymentMethodLabel(method: CashFlowItem['paymentMethod']): string {
    return {
      CASH: 'เงินสด',
      BANK_TRANSFER: 'โอนผ่านธนาคาร',
      CREDIT_CARD: 'บัตรเครดิต',
    }[method];
  }

  private feeResponsibilityLabel(
    responsibility: CashFlowItem['creditCardFeeResponsibility'],
  ): string {
    if (responsibility === 'CUSTOMER') return 'ลูกค้าชำระ';
    if (responsibility === 'COMPANY') return 'บริษัทรับภาระ';
    return 'ไม่ระบุ';
  }
}
